import { classnames } from '@aibees/shared/utils';
import { get } from 'lodash-es';
import { memo, useCallback, useEffect, useState } from 'react';
import { useAdvanceTableContext } from '../../context';
import { useTableLayoutTemplateContext } from '../table-layout-modifier-dialog/context';
import { TableStickyRow } from '../table-sticky-row/table-sticky-row';
import { useGetBrowseIdsApiData } from '../table/hooks';
import * as SX from './sx';
import type { TableStickyRowsWrapperProps as Props } from './types';

export const TableStickyRowsWrapper = memo(({ className }: Props) => {
  const {
    config,
    table,
    browseData,
    setStickyRowsHeight,
    stickyRowHeight,
    stickyCollapsed,
  } = useAdvanceTableContext();
  const [visibleRows, setVisibleRows] = useState<number[]>([]);
  const browseIdsApi = useGetBrowseIdsApiData(table);
  const { data } = browseData;
  const { config: modifiedConfig } = useTableLayoutTemplateContext();
  const columns = (modifiedConfig?.columns ?? config.columns).filter((x) => {
    // TODO remove it after testing column modifier
    // return !x.hide && !x.excludeFromColumnModifier;
    return !x.hide;
  });
  const checkboxEnabled = config.manualSelectionLimit
    ? table.selection.added.length < config.manualSelectionLimit
    : true;
  const rowKey = config.rowKey || 'id';

  // Sticky top offset for each row
  const stickyTopBase = 32; // Start position for the first sticky row

  useEffect(() => {
    if (stickyCollapsed) {
      // Always keep index 0 visible
      setVisibleRows([0]);

      // Remove other rows with staggered delay after animation
      const rows = data?.top?.length || 0;
      for (let i = 1; i < rows; i++) {
        setTimeout(() => {
          setVisibleRows((prev) => prev.filter((index) => index !== i));
        }, i * 100 + 300); // 100ms delay between each row + 300ms for animation
      }
    } else {
      // Show all rows immediately when expanding
      setVisibleRows(data?.top?.map((_, index) => index) || []);
    }
  }, [stickyCollapsed, data?.top]);

  const handleRowClick = useCallback(
    (item: any, key: string) => {
      config.onClickRow?.(item, key);
    },
    [config]
  );

  return (
    <>
      {!browseIdsApi?.isFetching &&
        data?.top?.map((item, index) => {
          const key = get(item, rowKey);
          const stickyTop =
            stickyRowHeight && stickyTopBase + index * stickyRowHeight;
          setStickyRowsHeight(stickyTop || 0);

          if (!visibleRows.includes(index)) return null;

          return (
            <TableStickyRow
              className={classnames('sticky')}
              key={key}
              data-key={key}
              item={item}
              index={index}
              columns={columns}
              config={config}
              selected={table.selection.isTopRowSelected(item.id)}
              checkboxEnabled={checkboxEnabled}
              sx={SX.stickyRow({
                stickyTop: stickyTop || 0,
                index,
                stickyCollapsed,
              })}
              onSelect={table.selection.onSelectTopRow}
              onRowClick={() => handleRowClick(item, key)}
            />
          );
        })}
    </>
  );
});

TableStickyRowsWrapper.displayName = 'TableStickyRowsWrapper';
